// action/store-data.tsx

import React, { useMemo } from "react";
import { CardShell, FieldLabel } from "./card-handler";
import { ChevronDown } from "lucide-react";
import { ActionNode } from "../../../../views/convoauto-pages/bot-studio/types";

interface StoreDataCardProps {
  node: ActionNode;
  onUpdate: (config: Record<string, any>) => void;
  onDelete: () => void;
  onReset: () => void;
  bare?: boolean;
}

const STORE_IN_OPTIONS = [
  { label: "Contact_Profile", value: "Contact_Profile" },
  { label: "Lead_Score",      value: "Lead_Score" },
  { label: "Tags",             value: "Tags" },
];

// Feedback Collection preset default rows. When Joseph opens the
// Store Data card and no rules are configured yet, we auto-populate
// with Good/Average/Poor pre-configured so the operator sees all 3
// branches on the SAME card without having to switch anything.
// Joseph 07-01 confirm: "Show all 3 branches (Good/Average/Poor) as
// separate rows on the same Store Data card, each pre-configured
// (Feedback == Good / Average / Poor)".
const DEFAULT_FEEDBACK_ROWS: Array<{ dataToStore: string; storeIn: string }> = [
  { dataToStore: "Feedback == Good",    storeIn: "Contact_Profile" },
  { dataToStore: "Feedback == Average", storeIn: "Contact_Profile" },
  { dataToStore: "Feedback == Poor",    storeIn: "Contact_Profile" },
];

export const StoreDataCard: React.FC<StoreDataCardProps> = ({
  node,
  onUpdate,
  onDelete,
  onReset,
  bare,
}) => {
  const config = node.config ?? {};

  // Multi-row model: config.rules is an array of {dataToStore, storeIn}.
  // Old single-row shape ({dataToStore, storeIn}) is auto-migrated on
  // first render so pre-existing journeys keep working.
  const rules: Array<{ dataToStore: string; storeIn: string }> = useMemo(() => {
    if (Array.isArray(config.rules) && config.rules.length > 0) {
      return config.rules.map((r: any) => ({
        dataToStore: String(r?.dataToStore ?? ""),
        storeIn:     String(r?.storeIn ?? "Contact_Profile"),
      }));
    }
    // Legacy single-row → wrap as 1-item array.
    if (config.dataToStore || config.storeIn) {
      return [{
        dataToStore: String(config.dataToStore ?? ""),
        storeIn:     String(config.storeIn ?? "Contact_Profile"),
      }];
    }
    // First render on a fresh Feedback Collection preset → pre-fill
    // with Good/Average/Poor so all 3 branches show simultaneously.
    return DEFAULT_FEEDBACK_ROWS.map((r) => ({ ...r }));
  }, [config.rules, config.dataToStore, config.storeIn]);

  const persistRules = (next: typeof rules) => {
    // Keep the first row's fields at the top level too so backend code
    // that reads config.dataToStore (existing wire format) still works
    // for the primary branch.
    const first = next[0] ?? { dataToStore: "", storeIn: "Contact_Profile" };
    onUpdate({
      ...config,
      rules: next,
      dataToStore: first.dataToStore,
      storeIn:     first.storeIn,
    });
  };

  const setRow = (idx: number, field: "dataToStore" | "storeIn", value: string) => {
    persistRules(rules.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  return (
    <CardShell
      title="Store Data"
      description="Store contact response data"
      onDelete={onDelete}
      onReset={onReset}
      bare={bare}
    >
        {/* Stacked rows — one per branch. All visible at once. */}
        <div className="space-y-3">
          {rules.map((row, idx) => (
            <div
              key={idx}
              className="border border-gray-100 dark:border-gray-800 rounded-lg p-2.5 bg-gray-50/40"
            >
              <FieldLabel>Data to store</FieldLabel>
              <input
                type="text"
                value={row.dataToStore}
                onChange={(e) => setRow(idx, "dataToStore", e.target.value)}
                placeholder="Feedback == Good"
                className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-300"
              />

              <div className="mt-2">
                <FieldLabel>Store In</FieldLabel>
                <div className="relative">
                  <select
                    value={row.storeIn}
                    onChange={(e) => setRow(idx, "storeIn", e.target.value)}
                    className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                  >
                    {STORE_IN_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    className="absolute right-3 top-2.5 text-[#7B7B7B] font-semibold dark:text-gray-500 pointer-events-none"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
    </CardShell>
  );
};